import { notifications } from '@mantine/notifications';
import { isLogoutPath, LOGOUT_PATH } from '../auth/logoutPaths';
import { APP_ERROR_PATH } from '../errors/reportAppError';
import {
  performLogoutSync,
  redirectToPortal,
  resolvePortalRedirectUrl,
} from './logout';

let redirectInProgress = false;

export function isAuthErrorStatus(status: number | undefined): boolean {
  // Session expiry only. Permission denials should use HTTP 403 and stay in-app.
  return status === 401;
}

function isOnTerminalAuthPath(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const path = window.location.pathname;
  return (
    isLogoutPath() ||
    path === APP_ERROR_PATH ||
    path.startsWith(`${APP_ERROR_PATH}/`)
  );
}

/**
 * On 401: clear the scheduler session and send the user to Portal2
 * (returnUrl when known, otherwise VITE_MAIN_PORTAL_BASEURL).
 * Avoids /error remount loops (permissions refetch → 401 → /error).
 */
export function handleSessionExpired(message?: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  // Already on logout/error — do not re-enter (prevents continuous reload).
  if (isOnTerminalAuthPath()) {
    return;
  }

  if (redirectInProgress) {
    return;
  }
  redirectInProgress = true;

  const detail =
    message?.trim() ||
    'Your session has expired. Sign in again from the main portal to continue.';

  const { returnUrl: savedReturnUrl } = performLogoutSync();
  const portalUrl = (savedReturnUrl || resolvePortalRedirectUrl()).trim();

  notifications.show({
    title: 'Session expired',
    message: portalUrl ? `${detail} Opening Portal2…` : detail,
    color: 'red',
    autoClose: 4000,
  });

  window.setTimeout(() => {
    try {
      if (portalUrl) {
        redirectToPortal(portalUrl);
        return;
      }
      // No portal URL configured — stop the ready-app API loop on /logout shell.
      window.location.assign(LOGOUT_PATH);
    } catch {
      redirectInProgress = false;
    }
  }, 600);
}
